import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NxDialogService, NxModalRef } from '@aposin/ng-aquila/modal';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { QbeIcons } from 'qbe-components';
import { TableComponentContentAligns } from 'qbe-components/lib/components/table/table.component.types';
import { Subject, filter, map, merge, takeUntil } from 'rxjs';
import { ApiState } from '../../model/api-state.model';
import { ModalAddonData, QuotationAddon, QuotationCoverage } from '../../model/offer';
import { ModalService, ModalType } from '../../services/modal.service';
import { ProcessType } from '../../services/process.service';
import { AppState } from '../../store';
import * as fromProcess from '../../store/process/process.reducer';
import { TrackingService } from '../../tracking/tracking.service';
import { formatterCurrency } from '../../utils';
import isAppAgentOrTravelAgency from '../../utils/is-agent-or-travel-agency.util';

export interface SwitcherStates {
  state: { [key: string]: boolean };
}

@Component({
  selector: 'app-offer-content',
  templateUrl: './offer-content.component.html',
  styleUrls: ['./offer-content.component.scss'],
})
export class OfferContentComponent implements OnInit, OnDestroy {
  @ViewChild('addOnTemplate') addOnTemplateRef!: TemplateRef<any>;
  @ViewChild('extendedInfoTemplate') extendedInfoTemplateRef!: TemplateRef<any>;
  @Input() showToolbox: boolean | undefined;

  selectedPackage$ = this.store.select(fromProcess.selectOfferSelectedPackage);
  premiumCalculationState$ = this.store.select(fromProcess.selectPremiumCalculationState);
  premiumCalculationData$ = this.store.select(fromProcess.selectPremiumCalculationData);
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  isResignation$ = this.processKind$.pipe(map((processKind) => processKind === ProcessType.RESIGNATION));
  discountCodeIsValid$ = this.store.select(fromProcess.selectOfferDiscountCodeIsValid);
  packageAddons$ = this.store.select(fromProcess.selectPackageAddons);
  offerValue$ = this.store.select(fromProcess.selectOfferValue);
  offerValueBeforeDiscounts$ = this.store.select(fromProcess.selectOfferValueBeforeDiscounts);
  insureds$ = merge(
    this.store.select(fromProcess.selectQuotationInsureds),
    this.store.select(fromProcess.selectPremiumCalculationInsureds),
  ).pipe(filter((insureds) => !!insureds));
  numberOfCustomizeAlcoholIncludedPersons$ = this.insureds$.pipe(
    map(
      (insureds) =>
        insureds?.filter((insured) => insured.coverages.some((coverage) => coverage.selected && coverage.codeName === 'AlcoholIncluded'))
          .length,
    ),
  );
  numberOfCustomizeForTheActivePersons$ = this.insureds$.pipe(
    map(
      (insureds) =>
        insureds?.filter((insured) => insured.coverages.some((coverage) => coverage.selected && coverage.codeName === 'ForTheActive'))
          .length,
    ),
  );

  ngDestroy$ = new Subject<void>();

  addOnModalRef?: NxModalRef<any>;
  extendedInfoModalRef?: NxModalRef<any>;

  protected readonly isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();
  protected readonly qbeIcons = QbeIcons;
  protected readonly apiState = ApiState;

  constructor(
    private store: Store<AppState>,
    private dialogService: NxDialogService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private trackingService: TrackingService,
  ) {}

  ngOnInit(): void {
    this.trackingImplementation();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  getExtraInfoButtonTranslation(extendedInfo: QuotationCoverage): string {
    switch (extendedInfo.codeName) {
      case 'Interruption':
        return 'offer.extendedInfo.btn.interrupt';
      case 'Cancellation':
        return 'offer.extendedInfo.btn.cancel';
      default:
        return 'offer.extendedInfo.btn.generic';
    }
  }

  isExtendedInfoModal(extendedInfo: QuotationCoverage): boolean {
    return ['Interruption', 'Cancellation'].includes(extendedInfo.codeName);
  }

  showCompareOffersModal(): void {
    this.trackingService.compare();
    this.modalService.openModal(ModalType.CompareOffers, { maxWidth: '1472px' });
  }

  changeSwitcher(value: boolean, addon: string): void {
    this.store.dispatch(
      fromProcess.actions.selectPackageAddons({
        addon,
        value,
      }),
    );
    if (addon === 'AlcoholIncluded') {
      this.store.dispatch(fromProcess.actions.setCustomizeAlcoholIncluded({ customizeAlcoholIncluded: [] }));
    }
    if (addon === 'ForTheActive') {
      this.store.dispatch(fromProcess.actions.setCustomizeForTheActiveIncluded({ customizeForTheActiveIncluded: [] }));
    }
  }

  showAddOnsModal(addon: QuotationAddon): void {
    this.trackingService.more(addon.codeName);

    const modalData: ModalAddonData = {
      header: addon.name,
      description: addon.additionalInformation,
    };

    if (addon.coverages.length) {
      modalData.tableData = [
        ['scope', 'amount', 'description'].map((param, index) => ({
          id: index.toString(),
          content: this.translateService.instant(`offer.addOns.modal.${param}-header`) as string,
          variant: 'header',
          contentAlign: param === 'amount' ? 'center' : 'left',
        })),
        ...addon.coverages.map((coverage) => [
          { id: '0', content: coverage.name, contentAlign: 'left' as TableComponentContentAligns },
          {
            id: '1',
            content: coverage.limit ? formatterCurrency(coverage.limit.amount, coverage.limit.currency) : '',
          },
          { id: '2', content: coverage.additionalInformations.additionalInfo, contentAlign: 'left' as TableComponentContentAligns },
        ]),
      ];
      modalData.colBackgroundColors = ['#FFF', '#FFF', '#FFF'];
    }

    this.addOnModalRef = this.dialogService.open(this.addOnTemplateRef, {
      panelClass: `${addon.codeName}-addOnModal`,
      showCloseIcon: true,
      data: modalData,
      maxWidth: '900px',
      width: '100%',
      maxHeight: '80vh',
    });
  }

  showExtendedInfoModal(extendedInfo: QuotationCoverage): void {
    const { codeName } = extendedInfo;
    this.trackingService.more(extendedInfo.codeName);

    switch (codeName) {
      case 'Interruption':
        this.modalService.openModal(ModalType.ResignationInterrupt);

        return;
      case 'Cancellation':
        this.modalService.openModal(ModalType.ResignationCancel);
    }
  }

  closeExtendedInfoModal(): void {
    this.extendedInfoModalRef?.close();
  }

  showCustomizeAlcoholIncludedModal() {
    this.trackingService.edit('AlcoholIncluded');
    this.modalService.openModal(ModalType.CustomizeAlcohol);
  }

  showCustomForTheActiveModal() {
    this.trackingService.edit('ForTheActive');
    this.modalService.openModal(ModalType.CustomizeForTheActive);
  }

  private trackingImplementation(): void {
    this.packageAddons$.pipe(takeUntil(this.ngDestroy$)).subscribe((addons) => {
      this.trackingService.extensions(addons.state);
    });
  }
}
